import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && el.classList.add("in-view")),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function ProjectsSection() {
  const { projects } = useSettings();
  const [active, setActive] = useState(0);
  const ref = useReveal();
  const current = projects[active] || projects[0];

  return (
    <section ref={ref} className="reveal py-28 lg:py-36 bg-[#F5EFE6] relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <div className="section-eyebrow mb-4">Signature Address Collection</div>
            <h2 className="section-title font-display">Curated <span className="italic text-[#B8893C]">High-Rise</span> Residences.</h2>
          </div>
          <p className="max-w-md text-[#3a322a] leading-relaxed">
            Each Tamra address is a study in proportion, light and craft — designed to age gracefully and live generously. Explore our active portfolio.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Big visual */}
          <div className="lg:col-span-8 project-card aspect-[16/10] rounded-sm overflow-hidden bg-[#EBE0CE]">
            <img
              key={current.id}
              src={current.cover}
              alt={current.name}
              className="w-full h-full object-cover img-hover"
            />
            <div className="overlay"></div>
            <div className="absolute left-8 right-8 bottom-8 text-[#F5EFE6]">
              <div className="text-[11px] tracking-[0.5em] uppercase text-[#B8893C] mb-3">{current.status}</div>
              <div className="flex items-end justify-between gap-6">
                <div>
                  <h3 className="font-display text-3xl md:text-5xl tracking-[0.08em]">{current.name}</h3>
                  <div className="text-sm md:text-base text-[#E8DCC4] mt-2">{current.location} · {current.type}</div>
                </div>
                <Link to="/projects" className="btn-gold hidden md:inline-flex">
                  View <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-4 flex flex-col">
            <div className="divide-y divide-[#EBE0CE] border-y border-[#EBE0CE]">
              {projects.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActive(i)}
                  className={`w-full text-left py-6 group transition ${active === i ? "bg-[#EBE0CE]/40" : "hover:bg-[#EBE0CE]/30"}`}
                >
                  <div className="flex items-center justify-between px-2">
                    <div>
                      <div className="font-display text-2xl tracking-[0.08em] text-[#1F1B17]">{p.name}</div>
                      <div className="text-xs uppercase tracking-[0.3em] text-[#9C7A3F] mt-2">{p.location}</div>
                    </div>
                    <ArrowUpRight
                      size={20}
                      className={`text-[#5C2018] transition-transform ${active === i ? "translate-x-1 -translate-y-1" : "opacity-50 group-hover:opacity-100"}`}
                    />
                  </div>
                </button>
              ))}
            </div>
            <p className="text-sm text-[#3a322a] leading-relaxed mt-8">{current.description}</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {current.highlights.map((h) => (
                <span key={h} className="px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase border border-[#B8893C]/50 text-[#5C2018] rounded-sm">{h}</span>
              ))}
            </div>
            <Link to="/projects" className="btn-primary mt-10 self-start md:hidden">
              View Project <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
